import { useMemo } from 'react';
import {
  Alert,
  AlertVariants,
  AlertAppearances,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { FieldSection } from '@/components/common';
import styles from './ContractOverviewTabView.module.scss';

/**
 * Props:
 *   contract  the contract detail view model
 */
export const ContractOverviewTabView = ({ contract }) => {
  const t = useTranslations();

  const declarationSectionFields = useMemo(
    () => [
      {
        label: t('FIELD_CONTRACTED_QUANTITY'),
        value: contract.declarationQuantities.contracted,
      },
      { label: t('FIELD_SHIPPED_QUANTITY'), value: contract.declarationQuantities.shipped },
      {
        label: t('FIELD_OUTSTANDING_QUANTITY'),
        value: contract.declarationQuantities.outstanding,
      },
    ],
    [t, contract.declarationQuantities],
  );

  const workingSectionFields = useMemo(
    () => [
      {
        label: t('FIELD_CONTRACTED_QUANTITY'),
        value: contract.workingQuantities.contracted,
      },
      { label: t('FIELD_SHIPPED_QUANTITY'), value: contract.workingQuantities.shipped },
      {
        label: t('FIELD_OUTSTANDING_QUANTITY'),
        value: contract.workingQuantities.outstanding,
      },
    ],
    [t, contract.workingQuantities],
  );

  // Figma 5882:61535 — it closes the working column, under the dashes it explains.
  const workingUnitAlert = useMemo(
    () =>
      contract.workingUnitMessage ? (
        <Alert variant={AlertVariants.warning} appearance={AlertAppearances.accent}>
          {contract.workingUnitMessage}
        </Alert>
      ) : null,
    [contract.workingUnitMessage],
  );

  const preferenceFields = useMemo(
    () => [
      { label: t('FIELD_GHG_EMISSIONS'), value: contract.ghgEmissions },
      { label: t('FIELD_GHG_EMISSION_SAVINGS'), value: contract.ghgEmissionSavings },
      { label: t('FIELD_RAW_MATERIAL_TYPE'), value: contract.rawMaterialType },
      { label: t('FIELD_COUNTRY_OF_ORIGIN'), value: contract.countryOfOrigin },
      { label: t('FIELD_FEEDSTOCK_CATEGORY'), value: contract.feedstockCategory },
      { label: t('FIELD_ALLOCATION_PREFERENCE'), value: contract.allocationPreference },
    ],
    [t, contract],
  );

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <Typography
          variant={TypographyVariants.bodySm}
          weight={TypographyWeights.semibold}
          color={TypographyColors.primary}
        >
          {t('CONTRACTS_DETAIL_ORDER_DETAILS')}
        </Typography>
      </div>
      <div className={styles.cardBody}>
        {contract.declarationUnit && (
          <FieldSection
            heading={t('SECTION_QUANTITY_IN_UNIT', { unit: contract.declarationUnit })}
            fields={declarationSectionFields}
          />
        )}
        {contract.workingUnit && (
          <FieldSection
            heading={t('SECTION_QUANTITY_IN_UNIT', { unit: contract.workingUnit })}
            fields={workingSectionFields}
            footer={workingUnitAlert}
          />
        )}
        <FieldSection heading={t('SECTION_PREFERENCES')} fields={preferenceFields} />
      </div>
    </div>
  );
};
